import CryptoJS from 'crypto-js';
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js';

const SHA256_HEX_REGEX = /^[a-f0-9]{64}$/i;

export const sha256 = (value: string): string => {
    return CryptoJS.SHA256(value).toString(CryptoJS.enc.Hex);
};

export const isSha256Hex = (value?: string): boolean => {
    if (!value) return false;
    return SHA256_HEX_REGEX.test(value.trim());
};

export const hashIfNeeded = (value?: string): string => {
    if (!value) return '';
    const trimmed = value.trim();
    return isSha256Hex(trimmed) ? trimmed.toLowerCase() : sha256(trimmed);
};

export const normalizeEmail = (email: string): string => {
    if (!email) return '';

    const [rawLocal, domain] = email.trim().toLowerCase().split('@');

    let local = rawLocal;

    if (domain === 'gmail.com' || domain === 'googlemail.com') {
        local = local.replace(/\./g, '').replace(/\+.*/, '');
    }

    return `${local}@${domain}`;
};

export const normalizeString = (value: string): string => {
    if (!value) return '';
    return value.trim().toLowerCase();
};

export const normalizePhone = (phone?: string, country?: CountryCode): string => {
    if (!phone) return '';

    try {
        const parsed = parsePhoneNumber(phone, country);

        if (!parsed || !parsed.isValid()) return '';

        const e164 = parsed.number;

        return e164.replace(/\D/g, '');
    } catch {
        return '';
    }
};

export type GTMUserData = {
    user_id?: string;
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    street?: string;
    city?: string;
    country?: string;
    postal_code?: string;
};

export const formatUserData = (raw: GTMUserData): Partial<GTMUserData> => {
    const data: Partial<GTMUserData> = {};

    if (raw.user_id) data.user_id = raw.user_id;

    if (raw.email) {
        const email = normalizeEmail(raw.email);
        if (email) data.email = hashIfNeeded(email);
    }

    if (raw.phone) {
        const phone = normalizePhone(raw.phone, raw.country as CountryCode);

        if (phone) {
            data.phone = hashIfNeeded(phone);
        }
    }

    if (raw.first_name) {
        const firstName = normalizeString(raw.first_name);
        if (firstName) data.first_name = hashIfNeeded(firstName);
    }

    if (raw.last_name) {
        const lastName = normalizeString(raw.last_name);
        if (lastName) data.last_name = hashIfNeeded(lastName);
    }

    if (raw.street) {
        const street = normalizeString(raw.street);
        if (street) data.street = hashIfNeeded(street);
    }

    if (raw.city) {
        const city = raw.city.trim().toLowerCase();
        if (city) data.city = city;
    }

    if (raw.country) {
        const country = raw.country.trim().toUpperCase();
        if (country) data.country = country;
    }

    if (raw.postal_code) {
        const postalCode = raw.postal_code.trim();
        if (postalCode) data.postal_code = postalCode;
    }

    return data;
};
